import { MediaPermissionsError, MediaPermissionsErrorType, requestMediaPermissions } from 'mic-check'
import { useEffect, useState } from 'react'
import { sendLogs } from 'utils'

export const useMedia = (getDevices?: boolean) => {
  const [microphonePermissionStatus, setMicrophonePermissionStatus] = useState<PermissionState | 'deniedByOS' | ''>('')
  const [audioInputDevices, setAudioInputDevices] = useState<{ label: string; value: string }[]>([])
  const [audioOutputDevices, setAudioOutputDevices] = useState<{ label: string; value: string }[]>([])

  useEffect(() => {
    requestMicPermission()
  }, [])

  const updateDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices()

    if (microphonePermissionStatus === 'granted') {
      const newInputDevices = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({ label: device.label, value: device.deviceId }))

      setAudioInputDevices(newInputDevices)
    }

    const newOutputDevices = devices
      .filter((device) => device.kind === 'audiooutput')
      .map((device) => ({ label: device.label, value: device.deviceId }))
    setAudioOutputDevices(newOutputDevices)
  }

  useEffect(() => {
    if (getDevices) {
      (async () => await updateDevices())()
    }
  }, [microphonePermissionStatus, getDevices])

  const requestMicPermission = async () => {
    requestMediaPermissions({ audio: true, video: false })
      .then(() => {
        setMicrophonePermissionStatus('granted')
        return 'granted'
      })
      .catch((err: MediaPermissionsError) => {
        const { type, name, message } = err

        sendLogs({ message: { type, name, message } })

        if (
          type === MediaPermissionsErrorType.UserPermissionDenied ||
          (type === MediaPermissionsErrorType.Generic && name === 'NotAllowedError')
        ) {
          if (microphonePermissionStatus !== 'denied') setMicrophonePermissionStatus('denied')
          return 'denied'
        }
        if (type === MediaPermissionsErrorType.SystemPermissionDenied) {
          if (microphonePermissionStatus !== 'deniedByOS') setMicrophonePermissionStatus('deniedByOS')
          return 'deniedByOS'
        }
        return err
      })
  }

  return {
    microphonePermissionStatus,
    requestMicPermission,
    audioInputDevices,
    audioOutputDevices,
  }
}
